import Editor from "@monaco-editor/react";
import { Grid, Typography, Button, TextField, IconButton } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import UpdateCancel from "../../atoms/buttons/UpdateCancel";
import { invokePostService, handleSnackBar, setInnerScreenRender } from "../../../store/global/actions";
import { makeStyles } from '@material-ui/core/styles';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import DeleteIcon from '@material-ui/icons/Delete';
import SkeletonLoader from "../../atoms/loader/SkeletonLoader";
import { useForm } from "react-hook-form";
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import DeleteUtilityFunction from "./DeleteUtilityFunctions";
import Requester from "../../../store/blueprint/svc";
const fetch = new Requester();

const useStyles = makeStyles((theme) => ({
    alertMessage: {
        width: '100%'
    },
    mainGrid: {
        margin: '0 !important',
        '& .MuiGrid-root:not(last-child)': {
            marginLeft: '0 !important',
            padding: '0 !important'
        }
    }
}));

export function UtilityFunctionFormEditor({ utilityFunctions, selectedUtilityFunction, handleClose, isFetching }) {
    const { register, handleSubmit, formState: { errors }, reset, setError } = useForm();

    const classes = useStyles();
    const editorRef = useRef(null);
    const containerRef = useRef(null);
    const dispatch = useDispatch();
    const [validationMsg, setValidationMsg] = useState(undefined);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isFullScreen, setFullScreen] = useState(false)
    const [disableDelete, setDisableDelete] = useState(false);
    const [functionName, setFunctionName] = useState("");
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [currentUtilityFunction, setCurrentUtilityFunction] = useState("");

    useEffect(() => {
        const defaultFunctionHeader = buildFunctionHeader(selectedUtilityFunction);
        setFunctionName(defaultFunctionHeader);
        reset({
            functionHeader: defaultFunctionHeader,
        });
        setCurrentUtilityFunction(selectedUtilityFunction?.body ? selectedUtilityFunction.body : prepareBoilerPlate());
        setDisableDelete(selectedUtilityFunction ? false : true);
    }, [selectedUtilityFunction])

    const buildFunctionHeader = (def) => {
        if (!def || !def.name) return "";

        const paramsStr = def.parameters
            ?.map((p) => p.name).join(", ");

        return `${def.name}(${paramsStr || ""})`;
    }


    const prepareBoilerPlate = () => {
        let boilerPlate = '--- Define function body here ---';
        return boilerPlate;
    }


    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        const editorElement = editor.getDomNode();
        if (editorElement) {
            editorElement.setAttribute('data-ui-tour-id', 'util-function-editor');
        }
    }

    const handleEditorChange = (value, event) => {
        setCurrentUtilityFunction(value);
    }

    const parseFunction = (input) => {
        const regex = /^([a-zA-Z_]\w*)\((.*)\)$/;
        const match = input.trim().match(regex);

        if (!match) return null;

        const name = match[1];
        const paramsRaw = match[2].trim();

        if (!paramsRaw) {
            return { name, params: [] };
        }

        const params = paramsRaw.split(",").map((p) => {
            const paramName = p.trim()
            return { name: paramName };
        });

        return { name, params };
    }

    const handleUtilityFunctionUpdate = (utilityFunction) => {
        setDisableDelete(true);
        const isUpdate = Boolean(selectedUtilityFunction);
        const serviceName = isUpdate ? 'updateUtilityFunction' : 'createUtilityFunction';
        const apiParams = isUpdate ? [selectedUtilityFunction._id] : [];
        dispatch(invokePostService(serviceName, { utilityFunction }, apiParams))
            .then(response => {
                setIsUpdating(false);
                if (response.status) {
                    setValidationMsg(undefined);
                    setDisableDelete(false);
                    handleClose();
                    dispatch(setInnerScreenRender());
                    const feedbackMessage = `Utility function ${isUpdate ? 'updated' : 'created'}`;
                    dispatch(handleSnackBar({
                        open: true,
                        message: feedbackMessage,
                        severity: 'success'
                    }));
                }
            });
    }

    const onSave = async ({ functionHeader }) => {
        setIsUpdating(true);
        const parsed = parseFunction(functionHeader);
        if (!parsed) {
            setError("functionHeader", { message: "Enter a valid function definition in the format: myFunction(arg1, arg2)" });
            return;
        }

        const code = editorRef.current.getValue();
        let validationResult = await fetch.post(fetch.route.luaValidate, { code });
        if (validationResult.status == false) {
            const errorMessage = validationResult.message.replace(/undefined/g, "").replace(/\[[^0-9](.*?)\]/g, "");
            setValidationMsg(errorMessage);
            setIsUpdating(false);
            return;
        } else {
            setValidationMsg(undefined);
        }

        const { name, params } = parsed;
        const utilityFunction = {
            name: name,
            body: code,
            parameters: params,
            type: 10,
        };

        handleUtilityFunctionUpdate(utilityFunction);
    };


    const saveAnyway = () => {
        setIsUpdating(true);
        const parsed = parseFunction(functionName);
        if (!parsed) {
            setError("functionHeader", { message: "Enter a valid function definition in the format: myFunction(arg1, arg2)" });
            return;
        }
        const code = editorRef.current.getValue();
        const { name, params } = parsed;
        const utilityFunction = {
            name: name,
            body: code,
            parameters: params,
            type: 10,
        };

        handleUtilityFunctionUpdate(utilityFunction);
    }


    const handleFullScreen = () => {
        const element = containerRef.current;
        if (document.fullscreenElement) {
            document.exitFullscreen();
            setFullScreen(false)
        } else {
            setFullScreen(true)
            element?.requestFullscreen();
        }
    }

    function onFullScreenChange() {
        if (!document.fullscreenElement && !document.webkitFullscreenElement && !document.mozFullScreenElement && !document.msFullscreenElement) {
            setFullScreen(false);
        }
    }

    const onDeleteUtilityFunction = () => {
        handleClose();
        dispatch(setInnerScreenRender());
    }

    const nameValidation = {
        required: "Function name is required",
        validate: {
            validNameStart: (value) =>
                /^[a-zA-Z_]\w*/.test(value) || "Invalid function name",
            uniqueName: (value) => {
                const fnName = value.split("(")[0].trim();
                return (
                    !utilityFunctions.some((f) => {
                        return f.name === fnName && f._id?.toString() !== selectedUtilityFunction?._id?.toString()
                    }) ||
                    "Function name already exists"
                );
            },
            hasBrackets: (value) =>
                /\(.*\)$/.test(value) || "Function must include parentheses",
            validName: (value) =>
                /^[a-zA-Z_]\w*\(.*\)$/.test(value) ||
                "Must be in format: myFunction(arg1, arg2)",
        },
    };

    const onChangeFunctionHeader = (e) => {
        let val = e.target.value;
        
        // Auto-close parentheses
        const openParens = (val.match(/\(/g) || []).length;
        const closeParens = (val.match(/\)/g) || []).length;
        if (openParens > closeParens && !val.endsWith(')')) {
          val += ')';
        }    
        setFunctionName(val);
      }

    document.addEventListener('fullscreenchange', onFullScreenChange);
    document.addEventListener('webkitfullscreenchange', onFullScreenChange);  // Safari
    document.addEventListener('mozfullscreenchange', onFullScreenChange);     // Firefox
    document.addEventListener('MSFullscreenChange', onFullScreenChange);      // IE/Edge


    return (
        <>
            <Grid container direction={"row"} ref={containerRef} spacing={1}>
                {!isFullScreen && <Grid item xs={12}>
                    <TextField
                        name="functionHeader"
                        id="functionHeader"
                        autoFocus
                        fullWidth
                        type={"text"}
                        required={true}
                        label={"Function Header"}
                        size={"small"}
                        InputLabelProps={{ shrink: true }}
                        placeholder={"normalizePayload(message,sensorID)"}
                        variant="outlined"
                        inputRef={register(nameValidation)}
                        value={functionName}
                        onChange={onChangeFunctionHeader}
                        error={!!errors?.functionHeader}
                        helperText={errors?.functionHeader?.message || ""}
                        data-ui-tour-id='util-function-header'
                    />
                </Grid>}
                {validationMsg !== undefined &&
                    <Grid item xs={12} >
                        <Alert severity="error" classes={{
                            message: classes.alertMessage,
                        }}>
                            <Grid container id="handlerErrorContainer">
                                <Grid item xs={12}>
                                    {validationMsg}
                                </Grid>
                                <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Button onClick={saveAnyway} disabled={isUpdating} style={{ backgroundColor: '#FFA726', color: '#fff' }} variant="contained">
                                        {isUpdating ? 'Updating..' : 'Update Anyway'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Alert>
                    </Grid>
                }

                <Grid item xs={12} className={`${classes.mainGrid}`}>
                    {isFetching ? (
                        <SkeletonLoader
                            type="code-editor"
                        />
                    ) : (
                        <>
                            <Grid style={{ display: "flex", justifyContent: "space-between", paddingRight: "10px !important", backgroundColor: '#d5ded6', width: '100%' }}>
                                <Typography id={"function_header_label"} style={{ padding: '5px 5px', fontWeight: 'bold', fontStyle: 'italic' }}> {`function ${functionName || ""}`} </Typography>
                                <Grid style={{ display: "flex", alignItems: "center" }}>
                                    {!isFullScreen && (
                                        <IconButton
                                            onClick={(e) => setOpenDeleteModal(true)}
                                            data-ui-tour-id="utility-function-delete-btn"
                                            disabled={disableDelete}
                                            color="secondary"
                                            id="delete_utility"
                                            style={{ padding: 6 }}
                                        >
                                            <DeleteIcon color="secondary" style={{ fontSize: '16px', color: "#1c548c" }} />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        id="delete_utility"
                                        style={{ padding: 6 }}
                                        color="secondary"
                                        onClick={handleFullScreen}
                                    >
                                        {!isFullScreen && <FullscreenIcon id="fullscreen-icon" data-ui-tour-id='util-function-fullscreen-btn' style={{ color: "#1c548c", fontSize: '20px', cursor: "pointer" }} />}
                                        {isFullScreen && <FullscreenExitIcon id="fullscreen-exit-icon" style={{ fontSize: '20px', cursor: "pointer", color: "#1c548c" }} />}
                                    </IconButton>
                                </Grid>
                            </Grid>
                            <Editor
                                height={isFullScreen ? '92vh' : "60vh"}
                                defaultLanguage="lua"
                                language="lua"
                                theme="vs-dark"
                                onMount={handleEditorDidMount}
                                onChange={handleEditorChange}
                                value={currentUtilityFunction}
                                options={{
                                    fontSize: 12, // Set initial font size to 12
                                    wordWrap: 'on', // Optional: Enable word wrap
                                    automaticLayout: true, // Adjusts to container size
                                    scrollBeyondLastLine: false, // Prevent extra scrolling
                                    mouseWheelZoom: true // Enable Ctrl + Scroll to zoom
                                }}
                                data-ui-tour-id='util-function-editor'
                            />
                            <Typography id="end-label" style={{ padding: '5px 5px', fontWeight: 'bold', fontStyle: 'italic', backgroundColor: '#d5ded6', marginTop: '-5px' }}> {"end"} </Typography>

                        </>
                    )}
                </Grid>

                {!isFullScreen && (
                    <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end" }} >
                        <UpdateCancel onUpdate={handleSubmit(onSave)} onCancel={handleClose} updateDisable={isUpdating || isFetching || validationMsg} content={selectedUtilityFunction ? isUpdating ? "Updating.." : "Update" : isUpdating ? "Creating.." : "Create"} />
                    </Grid>
                )}
            </Grid>

            {openDeleteModal &&
                <DeleteUtilityFunction onClose={() => setOpenDeleteModal(false)} utilityFunction={selectedUtilityFunction} onCompletion={onDeleteUtilityFunction} />
            }
        </>
    )
}